import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { modulo } from './modulo';
import { NO_VALUE_OPTION } from './form.helper';
import { MarkantInputDividedValue } from '@markant/elements';

export const validateIBAN: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value;

  if (!value) {
    return null;
  }

  let parsedValue = String(value);

  if (parsedValue.length < 15) {
    return { invalidIban: true };
  }

  const regexp = /^[a-zA-Z0-9]+$/;
  if (!regexp.test(parsedValue)) {
    return { invalidIban: true };
  }
  parsedValue = (parsedValue.substring(4) + parsedValue.substring(0, 4)).toUpperCase();
  let valueWithConvertedNumbers = '';
  for (let i = 0; i < parsedValue.length; i++) {
    const character = parsedValue.charCodeAt(i);

    if (character > 64 && character < 91) {
      valueWithConvertedNumbers += String(character - 55);
    } else {
      valueWithConvertedNumbers += String.fromCharCode(character);
    }
  }
  if (modulo(valueWithConvertedNumbers, 97) !== 1) {
    return { invalidIban: true };
  }

  return null;
};

export const glnLength = 13;

/**
 * This validator validates GLN by check digit.
 * A GLN is a 13-digit number and the last digit is so-called check digit.
 * Please read the following article to learn how to calculate if the check digit is correct.
 * https://ecosio.com/de/blog/gln-pruefziffer-berechnen
 *
 * @param control
 */
export const validateGLNCheckDigit: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const gln = control.value;
  if (!gln || gln.length < glnLength) {
    // should not validate until user inputs last digit
    return null;
  }

  return isValidCheckDigit(gln) ? null : { invalidGln: true };
};

export function isValidCheckDigit(gln: string): boolean {
  if (gln.length !== glnLength) {
    return false;
  }

  const glnArray = gln.split('').map(Number);
  const lastDigit = glnArray.pop();

  // calculate sum of each individual 12 digits. The place with even number should be multiplied by 3.
  const sum = glnArray.reduce((sum, digit, index) => sum + digit * ((index + 1) % 2 == 0 ? 3 : 1));

  // The rounded up sum minus sum should be equal to the check digit.
  const roundedSum = Math.ceil(sum / 10) * 10;
  return roundedSum - sum === lastDigit;
}

/**
 * This map comes from Billwerk. We hope to maintain same as original.
 *
 * Same map in organization service.
 * com.markant.organization.api.dto.validation.PatternValidationUtil#SALES_TAX_FORMAT_MAP
 */
const COUNTRY_VAT_ID_FORMAT_MAP = new Map<string, RegExp>([
  ['AT', /^ATU[A-Z0-9]{8}$/],
  ['BE', /^BE\d{10}$|^BE0\d{9}$/],
  ['BG', /^BG\d{9,10}$/],
  ['CH', /^CHE-?\d{3}.?\d{3}.?\d{3}$/], // Swiss is non EU
  ['CY', /^CY\d{8}[A-Z]$/],
  ['CZ', /^CZ\d{8,10}$/],
  ['DE', /^DE\d{9}$/],
  ['DK', /^DK\d{8}$/],
  ['EE', /^EE\d{9}$/],
  ['EL', /^EL\d{9}$/],
  ['ES', /^ES[A-Z0-9]\d{7}[A-Z0-9]$/],
  ['FI', /^FI\d{8}$/],
  ['FR', /^FR[A-Z0-9]{2}\d{9}$/],
  ['GB', /^GB\d{9}$|^GB\d{12}$|^GBGD\d{3}$|^GBHA\d{3}$/],
  ['XI', /^XI\d{9}$|^XI\d{12}$|^XIGD\d{3}$|^XIHA\d{3}$/],
  ['HR', /^HR\d{11}$/],
  ['HU', /^HU\d{8}$/],
  ['IE', /^IE\d[A-Z0-9]\d{5}[A-Z]$|^IE\d{7}[A-W][A-I]$/],
  ['IT', /^IT\d{11}$/],
  ['LT', /^LT\d{9}$|^LT\d{12}$/],
  ['LU', /^LU\d{8}$/],
  ['LV', /^LV\d{11}$/],
  ['MT', /^MT\d{8}$/],
  ['NL', /^NL[A-Z0-9]{9}B[A-Z0-9]{2}$/],
  ['PL', /^PL\d{10}$/],
  ['PT', /^PT\d{9}$/],
  ['RO', /^RO[1-9]\d{1,9}$/],
  ['SE', /^SE\d{12}$/],
  ['SI', /^SI\d{8}$/],
  ['SK', /^SK\d{10}$/],
]);

export const validateInputDividedVatId = (vatIdKey = 'salesTaxId'): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const inputDividedValue: MarkantInputDividedValue = control.value;

    if (inputDividedValue.selectValue !== vatIdKey || isValidVatId(inputDividedValue.inputValue)) {
      return null;
    }

    return { invalidVatId: true };
  };
};

export const validateVatId: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const vatId: string = control.value;

  return isValidVatId(vatId) ? null : { invalidVatId: true };
};

function isValidVatId(value: string): boolean {
  if (!value || value.length < 2) {
    return false;
  }

  const countryCode = value.substring(0, 2);

  return !!COUNTRY_VAT_ID_FORMAT_MAP.get(countryCode)?.test(value);
}

export const glnOrGlnCheckValidator: ValidatorFn = (control: AbstractControl): { glnMissing: boolean } | null => {
  if (!(control instanceof FormGroup)) {
    return null;
  }

  const glnControlValue: string | null = control.get('gln')?.value;
  const glnRequiredControlValue: boolean | null = control.get('glnNotAvailable')?.value;

  if (
    (glnControlValue != null && glnControlValue.length !== 0) ||
    (glnRequiredControlValue != null && glnRequiredControlValue)
  ) {
    return null;
  } else {
    return { glnMissing: true };
  }
};

/**
 * Validates all field of a given form to be required if at least one field contains a value.
 * When a field is being invalid, the error is set to the form control to enable proper error display.
 * Further a general form error will be returned.
 *
 * @param control A formGroup to be validated as a whole.
 */
export const allRequired: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!(control instanceof FormGroup)) {
    return null;
  }

  const keys = Object.keys(control.value);
  const values = Object.values(control.value);

  if (!values.every(isNonEmptyValue) && values.some(isNonEmptyValue)) {
    keys.forEach(key => {
      const field = control.controls[key];

      if (Validators.required(field)) {
        field.setErrors({ required: true });
      }

      if (field.value === NO_VALUE_OPTION) {
        field.setErrors({ pattern: true });
      }
    });

    return { allFieldsRequired: true };
  }

  keys.forEach(key => {
    const field = control.controls[key];

    if (field.hasError('required')) {
      field.setErrors(null);
    }
  });

  return null;
};

function isNonEmptyValue(value: any): boolean {
  return value !== null && value !== undefined && value !== '' && value !== NO_VALUE_OPTION;
}

export const notBlank: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  return (control.value || '').trim().length === 0 ? { required: true } : null;
};
